/**
 * Task wizard — step validation (single source of step order + logic)
 *
 * Component-free: per-step validators plus `buildTaskStepLogic`, the ordered
 * list of `{ id, requiredFields, validate }` that defines which steps exist and
 * in what order. The Related Tasks step is dropped when there are no other
 * tasks to link to; Summary is always last. Navigation/submission hooks index
 * this list by position so `currentStep` stays purely positional.
 *
 * The presentation half (icon/label/render) is zipped onto this list in
 * `config/wizardStepDescriptors.tsx`.
 */
import {
  type StepValidationContext,
  type StepValidationResult,
} from '@/components/common/wizard';

import type { MantineWizardFormData } from '../mantineFormSchema';
import type { InlineRequirement } from '../../types/data/InlineRequirement';
import { WIZARD_CONSTANTS } from '../../types/constants/constants';
import { FILE_VALIDATION_CONFIG } from '@/utils/fileValidation';
import { Priority } from '../../../../../types';

/** Stable section identifiers for the task wizard tabs. */
export type WizardStepId =
  | 'basicInfo'
  | 'details'
  | 'requirements'
  | 'relatedTasks';

/** Validation half of a wizard step (no presentation, no component imports). */
export interface TaskStepLogic {
  id: WizardStepId;
  requiredFields: (keyof MantineWizardFormData)[];
  validate: (
    context: StepValidationContext<MantineWizardFormData>
  ) => StepValidationResult;
}

type TaskStepContext = StepValidationContext<MantineWizardFormData>;

function validateBasicInfoStep({
  values,
  t,
}: TaskStepContext): StepValidationResult {
  const errors: Record<string, string> = {};

  if (!values.title?.trim()) {
    errors.title = t('validation.titleRequired');
  } else if (values.title.trim().length < WIZARD_CONSTANTS.MIN_TITLE_LENGTH) {
    errors.title = t('validation.titleMinLength');
  } else if (values.title.trim().length > WIZARD_CONSTANTS.MAX_TITLE_LENGTH) {
    errors.title = t('validation.titleMaxLength');
  }

  if (!values.priority || !Object.values(Priority).includes(values.priority)) {
    errors.priority = t('validation.priorityRequired');
  }

  // Description input lives on this step (basicInfo), so its length check does too.
  if (
    values.description &&
    values.description.length > WIZARD_CONSTANTS.MAX_DESCRIPTION_LENGTH
  ) {
    errors.description = t('validation.descriptionMaxLength');
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateDetailsStep({
  values,
  t,
}: TaskStepContext): StepValidationResult {
  const errors: Record<string, string> = {};

  const oversizedFiles =
    values.attachments?.filter(
      (file) => file.size > FILE_VALIDATION_CONFIG.MAX_FILE_SIZE
    ) || [];
  if (oversizedFiles.length > 0) {
    errors.attachments = t('validation.fileTooBig');
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateRequirementsStep({
  values,
  t,
}: TaskStepContext): StepValidationResult {
  const errors: Record<string, string> = {};

  if (values.requirements.length > WIZARD_CONSTANTS.MAX_REQUIREMENTS) {
    errors.requirements = t('validation.requirementsMaxCount');
  }

  const emptyRequirements = values.requirements.filter(
    (requirement: InlineRequirement) => !requirement.text.trim()
  );
  if (emptyRequirements.length > 0) {
    errors.requirements = t('validation.requirementRequired');
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

function validateRelatedTasksStep({
  values,
  t,
}: TaskStepContext): StepValidationResult {
  const errors: Record<string, string> = {};

  if (values.relatedTaskIds && !Array.isArray(values.relatedTaskIds)) {
    errors.relatedTaskIds = t('validation.invalidData');
  }

  if (
    values.requireRelatedTasksCompletion !== undefined &&
    typeof values.requireRelatedTasksCompletion !== 'boolean'
  ) {
    errors.requireRelatedTasksCompletion = t('validation.invalidData');
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

const BASIC_INFO_STEP: TaskStepLogic = {
  id: 'basicInfo',
  requiredFields: ['title', 'priority'],
  validate: validateBasicInfoStep,
};

const DETAILS_STEP: TaskStepLogic = {
  id: 'details',
  requiredFields: [],
  validate: validateDetailsStep,
};

const REQUIREMENTS_STEP: TaskStepLogic = {
  id: 'requirements',
  requiredFields: [],
  validate: validateRequirementsStep,
};

const RELATED_TASKS_STEP: TaskStepLogic = {
  id: 'relatedTasks',
  requiredFields: [],
  validate: validateRelatedTasksStep,
};

/**
 * Ordered section logic for the current wizard run. Related Tasks is dropped
 * when there are no other tasks to link to.
 */
export function buildTaskStepLogic(
  availableTasksCount: number
): TaskStepLogic[] {
  const steps: TaskStepLogic[] = [BASIC_INFO_STEP, REQUIREMENTS_STEP];
  if (availableTasksCount > 0) {
    steps.push(RELATED_TASKS_STEP);
  }
  steps.push(DETAILS_STEP); // attachments always last
  return steps;
}
